import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useJobStore } from '@core/job/useJobStore';
import { useDaySequenceStore } from '@core/daySequence/useDaySequenceStore';
import { useClientStore } from '@core/client/useClientStore';
import { useTenantStore } from '@core/tenant/useTenantStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import { useThemeStore } from '@core/theme/useThemeStore';
import { DaySequenceStatus, JobStatus } from '@core/generated/gql-types';
import { Colors } from '@core/config';
import {
  SearchBar,
  FilterDropdown,
  AppliedFilters,
} from '@/components/filters';
import {
  ACTIVE_ON_QUERY_PARAM,
  TODAYS_ACTIVE_JOB_STATUSES,
  createJobFilterConfig,
  getJobAccountManagerLabel,
  getJobClassificationGroupName,
  getJobClassificationName,
  getJobSiteLabel,
  jobMatchesApplicationFilters,
} from '@/modules/jobs/utils/jobFilters.utils';
import { useJobsTable } from '@/modules/jobs/hooks/useJobsTable';
import JobsTable from '@/modules/jobs/components/JobsTable';
import { Skeleton, SkeletonTable } from '@/components/shared/Skeleton';
import JobDrawer from '@/modules/jobs/components/JobDrawer';
import { useTenantId } from '@/contexts/TenantContext';
import Button from '@/components/Button';
import DateRangePicker from '@/components/DateRangePicker';
import useFeatureView from '@/hooks/useFeatureView';
import { useJobsListData } from '@/modules/jobs/hooks/useJobsListData';
import { getJobDrawerView } from '@/modules/jobs/views/jobDrawer.view';
import { getJobsListView } from '@/modules/jobs/views/jobsList.view';
import { requestPredictiveShiftReportExport } from '@core/report/report.action';
import { toast } from 'react-toastify';
import { formatAccountManagerDisplay } from '@/utils/client.constants';
import { getCustomAttribute } from '@/utils/utils';

const JobsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeOn = searchParams.get(ACTIVE_ON_QUERY_PARAM);
  const hasAppliedActiveOnFilters = useRef(false);
  const theme = useThemeStore();
  const { getJobById, jobs } = useJobStore();
  const tenantId = useTenantId();
  const view = useFeatureView(getJobsListView);
  const drawerView = useFeatureView(getJobDrawerView);
  const { filterKeys, table, actions } = view;
  const {
    clientId,
    dateRange,
    commitDateRange,
    jobsInSelectedDateRange,
    isLoading,
    isFetchingMore,
    jobsLoading,
    hasMoreFromServer,
    fetchNextPage,
  } = useJobsListData(view);

  const [isExportingPredictiveReport, setIsExportingPredictiveReport] =
    useState(false);
  const { getClientById, clients } = useClientStore();
  const tenantUsers = useTenantStore((state) => state.tenantUsers);
  const fetchTenantUsersForTenant = useTenantStore(
    (state) => state.fetchTenantUsersForTenant,
  );
  const { classificationGroups, getClassificationById } =
    useClassificationStore();
  const { byId: daySequenceById, idsByJobId: daySequenceIdsByJobId } =
    useDaySequenceStore((state) => state);

  const [filters, setFilters] = useState<Record<string, string[]>>({
    client: [],
    site: [],
    industry: [],
    skillSet: [],
    application: [],
    daySequenceStatus: [],
    accountManager: [],
  });
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const selectedJob = useMemo(
    () =>
      selectedJobId
        ? (jobs.find((job) => job.id === selectedJobId) ??
          getJobById(selectedJobId))
        : null,
    [selectedJobId, jobs, getJobById],
  );

  const accountManagerByClientId = useMemo(() => {
    const names = new Map<string, string>();
    clients.forEach((client) => {
      const accountManager = getCustomAttribute(client, 'accountManager');
      if (!accountManager) {
        return;
      }
      const display = formatAccountManagerDisplay(accountManager, tenantUsers);
      if (display !== '-') {
        names.set(client.id, display);
      }
    });
    return names;
  }, [clients, tenantUsers]);

  useEffect(() => {
    if (!tenantId || !table.showAccountManagerColumn) return;
    void fetchTenantUsersForTenant(tenantId);
  }, [tenantId, table.showAccountManagerColumn, fetchTenantUsersForTenant]);

  const jobFilterConfig = React.useMemo(() => {
    const allFilters = createJobFilterConfig({
      getClientById,
      classificationGroups,
      getClassificationById,
      accountManagerByClientId,
    });
    return allFilters.filter((config) =>
      filterKeys.includes(config.key as (typeof filterKeys)[number]),
    );
  }, [
    getClientById,
    classificationGroups,
    getClassificationById,
    accountManagerByClientId,
    filterKeys,
  ]);

  const classificationFilterParams = useMemo(
    () => ({ classificationGroups, getClassificationById }),
    [classificationGroups, getClassificationById],
  );

  const jobsForTable = useMemo(() => {
    return jobsInSelectedDateRange.filter((job) => {
      const clientName = getClientById(job.clientId ?? '')?.name;
      const clientMatch =
        filters.client.length === 0 ||
        (clientName && filters.client.includes(clientName));
      const siteLabel = getJobSiteLabel(job);
      const siteMatch =
        filters.site.length === 0 || filters.site.includes(siteLabel);
      const industryName = getJobClassificationGroupName(
        job,
        classificationFilterParams,
      );
      const industryMatch =
        filters.industry.length === 0 ||
        (industryName !== '-' && filters.industry.includes(industryName));
      const skillSetName = getJobClassificationName(
        job,
        classificationFilterParams,
      );
      const skillSetMatch =
        filters.skillSet.length === 0 ||
        (skillSetName !== '-' && filters.skillSet.includes(skillSetName));

      const daySequenceStatusFilters = filters.daySequenceStatus ?? [];
      const jobId = job.id ?? '';
      const daySequenceIds = daySequenceIdsByJobId[jobId] ?? [];
      const daySequenceStatusesForJob = daySequenceIds
        .map((sequenceId) => daySequenceById[sequenceId]?.status)
        .filter((status: DaySequenceStatus) => status != null);
      const daySequenceStatusMatch =
        daySequenceStatusFilters.length === 0 ||
        daySequenceStatusesForJob.some((status: DaySequenceStatus) =>
          daySequenceStatusFilters.includes(status),
        );

      const applicationMatch = jobMatchesApplicationFilters(
        job,
        filters.application ?? [],
      );

      const accountManagerFilters = filters.accountManager ?? [];
      const accountManagerName = getJobAccountManagerLabel(
        job.clientId,
        accountManagerByClientId,
      );
      const accountManagerMatch =
        accountManagerFilters.length === 0 ||
        accountManagerFilters.includes(accountManagerName);

      return (
        clientMatch &&
        siteMatch &&
        industryMatch &&
        skillSetMatch &&
        daySequenceStatusMatch &&
        applicationMatch &&
        accountManagerMatch
      );
    });
  }, [
    jobsInSelectedDateRange,
    filters.client,
    filters.site,
    filters.industry,
    filters.skillSet,
    filters.application,
    filters.daySequenceStatus,
    filters.accountManager,
    accountManagerByClientId,
    daySequenceById,
    daySequenceIdsByJobId,
    getClientById,
    classificationFilterParams,
  ]);

  const {
    sortField,
    sortDirection,
    searchQuery,
    setSearchQuery,
    statusFilters,
    setStatusFilters,
    handleSort,
    paginatedFilteredJobs,
    pagination,
  } = useJobsTable(jobsForTable);

  useEffect(() => {
    if (!activeOn || hasAppliedActiveOnFilters.current) return;
    hasAppliedActiveOnFilters.current = true;
    setStatusFilters(TODAYS_ACTIVE_JOB_STATUSES);
  }, [activeOn, setStatusFilters]);

  const setFilterByKey = useCallback((key: string) => {
    return (values: string[]) => {
      setFilters((prev) => ({ ...prev, [key]: values }));
    };
  }, []);

  const handleJobClick = (jobId: string) => {
    setSelectedJobId(jobId);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedJobId(null);
  };

  const handlePageChange = useCallback(
    (page: number) => {
      pagination.onPageChange(page);

      const isGoingToLastPage = page >= pagination.totalPages;
      if (isGoingToLastPage && hasMoreFromServer && !isFetchingMore) {
        void fetchNextPage();
      }
    },
    [pagination, hasMoreFromServer, isFetchingMore, fetchNextPage],
  );

  const handleExportPredictiveShiftReport = useCallback(async () => {
    if (!tenantId) {
      return;
    }

    setIsExportingPredictiveReport(true);
    try {
      const startDate = String(dayjs(dateRange.start).startOf('day').valueOf());
      const endDate = String(dayjs(dateRange.end).endOf('day').valueOf());

      await requestPredictiveShiftReportExport({
        tenantId,
        startDate,
        endDate,
        includeUnfilledShifts: true,
      });

      toast.success(
        'Your predictive shift report is being generated. We will email you when it is ready.',
        { position: 'bottom-left', autoClose: 5000 },
      );
    } catch (error) {
      console.error('Error requesting predictive shift report export:', error);
      toast.error(
        'Failed to request predictive shift report export. Please try again.',
        { position: 'bottom-left', autoClose: 5000 },
      );
    } finally {
      setIsExportingPredictiveReport(false);
    }
  }, [tenantId, dateRange.start, dateRange.end]);

  const handleDateRangeChange = useCallback(
    (start: Date, end: Date) => {
      if (searchParams.get(ACTIVE_ON_QUERY_PARAM)) {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete(ACTIVE_ON_QUERY_PARAM);
        setSearchParams(nextSearchParams, { replace: true });
      }
      commitDateRange({ start, end });
    },
    [commitDateRange, searchParams, setSearchParams],
  );

  const handleStatusFiltersChange = useCallback(
    (nextStatusFilters: string[]) => {
      setStatusFilters(nextStatusFilters as JobStatus[]);
    },
    [setStatusFilters],
  );

  const appliedFilterGroups = useMemo(
    () =>
      [
        {
          key: 'status',
          label: 'Status',
          values: statusFilters,
          setValues: handleStatusFiltersChange,
        },
        {
          key: 'client',
          label: 'Client',
          values: filters.client ?? [],
          setValues: setFilterByKey('client'),
        },
        {
          key: 'accountManager',
          label: 'Account manager',
          values: filters.accountManager ?? [],
          setValues: setFilterByKey('accountManager'),
          formatLabel: (value: string) => value,
        },
        {
          key: 'site',
          label: 'Site',
          values: filters.site ?? [],
          setValues: setFilterByKey('site'),
        },
        {
          key: 'industry',
          label: 'Industry',
          values: filters.industry ?? [],
          setValues: setFilterByKey('industry'),
        },
        {
          key: 'skillSet',
          label: 'Skill set',
          values: filters.skillSet ?? [],
          setValues: setFilterByKey('skillSet'),
        },
        {
          key: 'application',
          label: 'Application',
          values: filters.application ?? [],
          setValues: setFilterByKey('application'),
        },
        {
          key: 'daySequenceStatus',
          label: 'Timesheet status',
          values: filters.daySequenceStatus ?? [],
          setValues: setFilterByKey('daySequenceStatus'),
        },
      ].filter((group) =>
        filterKeys.includes(group.key as (typeof filterKeys)[number]),
      ),
    [
      statusFilters,
      filters,
      setFilterByKey,
      handleStatusFiltersChange,
      filterKeys,
    ],
  );

  if ((isLoading || jobsLoading) && jobs?.length === 0) {
    return (
      <div className="flex h-full flex-col p-6">
        <div className="mb-4 flex items-center justify-between">
          <Skeleton height="h-8" width="w-24" />
        </div>

        <div className="mb-2 flex items-center gap-2">
          <Skeleton height="h-10" width="w-64" />
          <Skeleton height="h-10" width="w-32" />
        </div>

        <SkeletonTable rows={8} columns={6} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-4 flex items-center justify-between">
        {actions.showDateRangePicker && (
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            placeholder="Date range"
            primaryBrandColor={
              theme?.theme?.primaryBrandColor || Colors.LINK_BLUE
            }
            panelMargin="mt-2"
          />
        )}
        <div className="flex items-center gap-2">
          {actions.showExportReport && (
            <button
              type="button"
              onClick={handleExportPredictiveShiftReport}
              disabled={isExportingPredictiveReport || !tenantId}
              className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-xs transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FontAwesomeIcon icon={faFileLines} className="text-gray-500" />
              {isExportingPredictiveReport ? 'Exporting…' : 'Export Report'}
            </button>
          )}
          {actions.showAddJob && (
            <Button
              variant="primary"
              onClick={() => navigate('/jobs/new')}
              className="px-4 py-2 text-sm font-medium shadow-xs"
            >
              + Post a job
            </Button>
          )}
        </div>
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          placeholder="Search jobs..."
        />
        {jobFilterConfig.map((config) => (
          <FilterDropdown
            key={config.key}
            title={config.title}
            data={jobsInSelectedDateRange}
            getOptions={config.getOptions}
            selectedFilters={
              config.key === 'status'
                ? statusFilters
                : (filters[config.key] ?? [])
            }
            setSelectedFilters={
              config.key === 'status'
                ? handleStatusFiltersChange
                : setFilterByKey(config.key)
            }
            formatLabel={
              config.key === 'accountManager'
                ? (value: string) => value
                : undefined
            }
          />
        ))}
      </div>

      <AppliedFilters filterGroups={appliedFilterGroups} />

      <JobsTable
        jobs={paginatedFilteredJobs}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        onJobClick={handleJobClick}
        showClientColumn={table.showClientColumn}
        showAccountManagerColumn={table.showAccountManagerColumn}
        accountManagerByClientId={accountManagerByClientId}
        showMarginColumn={table.showMarginColumn}
        showCandidateColumn={table.showCandidateColumn}
        showApplicationsColumn={table.showApplicationsColumn}
        workersClientId={clientId}
        pagination={{
          ...pagination,
          onPageChange: handlePageChange,
          hasMore: hasMoreFromServer || pagination.hasMore,
        }}
        isLoading={isLoading || isFetchingMore}
      />

      <JobDrawer
        job={selectedJobId ? getJobById(selectedJobId) : null}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        view={drawerView}
      />
    </div>
  );
};

export default JobsPage;
